<template>
  <div>
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-yellow"></div>
    </div>
    <template v-else>
      <div class="relative overflow-x-auto shadow-md sm:rounded-lg mt-5">
        <table class="w-full text-left rtl:text-right text-gray-700">
          <thead>
            <tr class="bg-gray-100">
              <th scope="col" class="px-6">
                <div class="flex items-center">
                  Facility
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">
                  Units Sent
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">
                  Issued By
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">
                  Date Issued
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center"></div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="unit in unitTransfers" :key="unit.unit_transfer_id">
              <!-- Main Row -->
              <tr class="border-b">
                <td class="px-6 py-2">{{ unit.facility }}</td>
                <td class="px-6 py-2">{{ unit.unit_count }}</td>
                <td class="px-6 py-2">{{ unit.user?.first_name }} {{ unit.user?.last_name }}</td>
                <td class="px-6 py-2">{{ formatDate(unit.issued_date) }}</td>
                <td class="px-6 py-2 flex items-center">
                  <button @click="toggleExpand(unit.unit_transfer_id)"
                    class="flex items-center text-sm font-semibold text-blue-600 hover:underline">
                    <svg :class="{
                      'rotate-180 text-indigo-600': expandedTransferId === unit.unit_transfer_id,
                      'text-gray-900': expandedTransferId !== unit.unit_transfer_id,
                    }" class="w-5 h-5 transition-transform duration-300" viewBox="0 0 22 22" fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path d="M16.5 8.25L12.4142 12.3358C11.7475 13.0025 11.4142 13.3358 11 13.3358C10.5858 13.3358 10.2525 13.0025 9.58579 12.3358L5.5 8.25"
                        stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </button>
                </td>
              </tr>

              <!-- Expanded Products Row -->
              <tr v-if="expandedTransferId === unit.unit_transfer_id">
                <td colspan="6">
                  <div class="p-4 bg-gray-50 border-l-4 border-orange-200 rounded-md m-3">
                    <h3 class="text-md font-semibold text-gray-700">Transfer Products</h3>
                    <table class="w-full text-left mt-2">
                      <thead>
                        <tr class="bg-gray-200">
                          <th class="px-4 py-2">Pint Serial</th>
                          <th class="px-4 py-2">Volume (ml)</th>
                          <th class="px-4 py-2">Blood Group</th>
                          <th class="px-4 py-2">Product Type</th>
                          <th class="px-4 py-2">Expiry Date</th>
                          <th class="px-4 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="product in unit.unit_transfer_products" :key="product.unit_transfer_product_id"
                          class="border-t">
                          <td class="px-4 py-2">{{ product.product.pint_serial }}</td>
                          <td class="px-4 py-2">{{ product.product.volume }}</td>
                          <td class="px-4 py-2">{{ product.product.blood_group.description }}</td>
                          <td class="px-4 py-2">{{ product.product.unit_product.name }}</td>
                          <td class="px-4 py-2">{{ formatDate(product.product.expiry_date) }}</td>
                          <td class="px-4 py-2">
                            <span class="px-2 py-2 font-semibold text-gray-700 bg-gray-100 rounded">
                              {{ product.status }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div class="flex justify-center gap-4 mt-4">
        <button @click="previousPage" :disabled="currentPage === 1"
          class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button">
          <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
            viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m15 19-7-7 7-7" />
          </svg>
          Previous
        </button>

        <div class="flex items-center gap-2">
          <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="{
            'bg-custom-blue text-white': page === currentPage,
            'text-custom-blue': page !== currentPage,
          }"
            class="relative h-10 max-h-[40px] w-10 max-w-[40px] select-none rounded-lg text-center align-middle font-medium transition-all hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
            type="button">
            <span class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
              {{ page }}
            </span>
          </button>
        </div>

        <button @click="nextPage" :disabled="currentPage === totalPages"
          class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button">
          Next
          <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
            viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m9 5 7 7-7 7" />
          </svg>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import api from "@/services/api";

export default {
  name: "ExternalUnitIssuanceTab",
  components: {},
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
    filters: {
      type: Object,
      default: () => ({
        blood_group: "",
        status: "",
        unit_product: "",
        start_date: "",
        end_date: ""
      })
    }
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      perPage: 10,
      loading: false,
      unitTransfers: [],
      expandedTransferId: null,
      error: null,
      facility_logo: '',
      localSearchQuery: ''
    };
  },
  created() {
    this.getUnitTransfers();
    this.user = this.$store.getters["auth/user"];
    this.facility_logo = this.user.user_roles[0]?.facility_department?.facility?.logo;
    this.localSearchQuery = this.searchQuery;
  },
  computed: {
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];
      let l;

      for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
          range.push(i);
        }
      }

      range.forEach(i => {
        if (l) {
          if (i - l === 2) {
            rangeWithDots.push(l + 1);
          } else if (i - l !== 1) {
            rangeWithDots.push('...');
          }
        }
        rangeWithDots.push(i);
        l = i;
      });

      return rangeWithDots;
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1;
        this.getUnitTransfers();
      }
    },
    searchQuery: {
      handler(newVal) {
        this.localSearchQuery = newVal;
        this.currentPage = 1;
        this.getUnitTransfers();
      },
      immediate: true
    },
    localSearchQuery: {
      handler(newVal) {
        this.$emit('update:searchQuery', newVal);
        this.currentPage = 1;
        this.getUnitTransfers();
      }
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return 'N/A';
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        console.error("Invalid date:", date);
        return 'N/A';
      }
      return parsedDate.toLocaleString();
    },

    applyFilters() {
      this.currentPage = 1;
      this.getUnitTransfers();
    },

    cancelFilters() {
      this.$emit('cancel-filters');
      this.currentPage = 1;
      this.getUnitTransfers();
    },

    toggleExpand(unitTransferId) {
      this.expandedTransferId = this.expandedTransferId === unitTransferId ? null : unitTransferId;
    },

    async getUnitTransfers() {
      this.loading = true;
      try {
        const queryParams = {
          blood_group: this.filters.blood_group,
          status: this.filters.status,
          unit_product: this.filters.unit_product,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          search_query: this.localSearchQuery,
          page: this.currentPage,
          per_page: this.perPage
        };

        const response = await api.getUnitTransfers(queryParams);
        this.unitTransfers = response.data || [];
        this.currentPage = response.current_page || 1;
        this.totalPages = response.total_pages || 1;
      } catch (error) {
        this.error = error.message || "Failed to fetch unit transfers";
        console.error("Error fetching unit transfers:", error);
      } finally {
        this.loading = false;
      }
    },

    goToPage(page) {
      if (page !== '...' && page !== this.currentPage) {
        this.currentPage = page;
        this.getUnitTransfers();
      }
    },

    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.getUnitTransfers();
      }
    },

    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        await this.getUnitTransfers();
      }
    },

    async handleDownloadExternalIssuedUnits() {
      const printWindow = document.createElement('iframe');
      printWindow.style.position = 'absolute';
      printWindow.style.width = '0';
      printWindow.style.height = '0';
      document.body.appendChild(printWindow);

      try {
        const queryParams = {
          blood_group: this.filters.blood_group,
          status: this.filters.status,
          unit_product: this.filters.unit_product,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          search_query: this.localSearchQuery,
          all: true
        };

        const response = await api.getUnitTransfers(queryParams);
        const unit_transfers = response.data || [];

        const tableRows = unit_transfers.map((unit_transfer, index) => {
          const mainRow = `
            <tr class="border-b text-sm main-transfer">
              <td class="px-3 py-1">${index + 1}</td>
              <td class="px-3 py-1">${unit_transfer.facility || ''}</td>
              <td class="px-3 py-1">${unit_transfer.unit_count || ''}</td>
              <td class="px-3 py-1">${unit_transfer.user?.first_name || ''} ${unit_transfer.user?.last_name || ''}</td>
              <td class="px-3 py-1">${this.formatDate(unit_transfer.issued_date) || 'N/A'}</td>
            </tr>`;

          const productsTable = `
            <tr class="border-b text-sm">
              <td colspan="5" class="p-0">
                <table class="w-full text-left">
                  <thead>
                    <tr class="bg-gray-100">
                      <th class="px-3 py-2">#</th>
                      <th class="px-3 py-2">Pint Serial</th>
                      <th class="px-3 py-2">Volume</th>
                      <th class="px-3 py-2">Blood Group</th>
                      <th class="px-3 py-2">Product Type</th>
                      <th class="px-3 py-2">Expiry Date</th>
                      <th class="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${unit_transfer.unit_transfer_products.map((product, productIndex) => `
                      <tr class="border-b text-sm product-details bg-gray-50">
                        <td class="px-1 py-1 pl-8">${productIndex + 1}</td>
                        <td class="px-1 py-1">${product.product.pint_serial || ''}</td>
                        <td class="px-1 py-1">${product.product.volume || ''} ml</td>
                        <td class="px-1 py-1">${product.product.blood_group?.description || ''}</td>
                        <td class="px-1 py-1">${product.product.unit_product?.name || ''}</td>
                        <td class="px-1 py-1">${this.formatDate(product.product.expiry_date) || 'N/A'}</td>
                        <td class="px-1 py-1">${product.status || ''}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </td>
            </tr>`;

          return mainRow + productsTable;
        }).join('');

        const printContent = `
          <div class="page-container">
            <div class="report-container">
              <div class="logo-container">
                <img src="${this.facility_logo}" alt="Hospital Logo" class="logo">
              </div>
              <br><br>
              <h1 class="text-lg font-bold mb-4">External Unit Issuance Report</h1>
              <p class="mb-4">Generated on: ${new Date().toLocaleString()}</p>
              <div class="table-container">
                <table class="w-full text-left">
                  <thead>
                    <tr class="bg-gray-100">
                      <th class="px-3 py-2">#</th>
                      <th class="px-3 py-2">Facility</th>
                      <th class="px-3 py-2">Units Sent</th>
                      <th class="px-3 py-2">Issued By</th>
                      <th class="px-3 py-2">Date Issued</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${tableRows}
                  </tbody>
                </table>
              </div>
              <div class="signatures mt-8">
                <div class="signature-line">
                  <p>Prepared by: _________________</p>
                  <p>Date: _________________</p>
                </div>
                <div class="signature-line mt-4">
                  <p>Verified by: _________________</p>
                  <p>Date: _________________</p>
                </div>
              </div>
            </div>
          </div>
        `;

        const printDocument = `
          <html>
          <head>
            <title>External Unit Issuance Report</title>
            <style>
              @media print {
                @page {
                  size: A4;
                  margin: 2cm;
                }
              }
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                color: #333;
                margin: 0;
                padding: 0;
              }
              .logo {
                width: 120px;
                float: right;
              }
              .page-container {
                padding: 20px;
              }
              .report-container {
                max-width: 100%;
                margin: 0 auto;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
                font-size: 10.5px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #F3F4F6;
                font-weight: bold;
              }
              .signatures {
                margin-top: 40px;
                page-break-inside: avoid;
                font-size: 10.5px;
              }
              .signature-line {
                margin-bottom: 20px;
              }
              .product-details {
                font-size: 0.9em;
                margin-left: 3px;
              }
              .main-transfer {
                font-weight: 500;
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
          </html>
        `;

        printWindow.contentDocument.open();
        printWindow.contentDocument.write(printDocument);
        printWindow.contentDocument.close();

        printWindow.contentWindow.onload = function () {
          printWindow.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(printWindow);
          }, 1000);
        };
      } catch (error) {
        console.error('Error generating report:', error);
      }
    }
  }
};
</script>

<style lang="css" scoped></style>